import React, { useState, useRef, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Mail, ArrowLeft, Loader2 } from "lucide-react";
import { userApiClient, ApiException } from "@/lib/userApiClient";

/**
 * 邮箱验证页面
 * Vercel 风格设计
 * 注意：此页面由 AuthLayout 包裹，不需要自己定义 header
 */
export default function VerifyEmail() {
  const { t } = useTranslation(["register", "common"]);
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(searchParams.get("email") || "");
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [countdown, setCountdown] = useState(0);

  const codeRefs = [
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
  ];
  // 使用 ref 防止 StrictMode 下重复显示 Toast
  const toastShownRef = useRef(false);

  // 如果 URL 中有 registered/redirect/email 参数，提示用户查收邮件
  useEffect(() => {
    if (toastShownRef.current) return;

    if (
      searchParams.get("registered") ||
      searchParams.get("redirect") ||
      searchParams.get("email")
    ) {
      toast.info(
        t("common.verifyEmailFirst", "请查收邮件并输入验证码完成验证")
      );
      toastShownRef.current = true;
    }

    // 如果有 registered 参数，将其清除，保持 URL整洁
    if (searchParams.get("registered")) {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete("registered");
      window.history.replaceState(
        {},
        "",
        `${window.location.pathname}?${newSearchParams.toString()}`
      );
    }
  }, []);

  // 倒计时
  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setInterval(() => setCountdown((c) => c - 1), 1000);
    return () => clearInterval(timer);
  }, [countdown]);

  // 自动聚焦到下一个输入框
  const handleInput = (index: number, value: string) => {
    if (value.length === 1 && index < 3) {
      codeRefs[index + 1].current?.focus();
    }
  };

  // 处理退格键
  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !codeRefs[index].current?.value && index > 0) {
      codeRefs[index - 1].current?.focus();
    }
  };

  // 验证邮箱
  const handleVerify = async () => {
    const code = codeRefs.map((ref) => ref.current?.value || "").join("");

    if (code.length !== 4) {
      toast.error(t("common.invalidCode"));
      return;
    }

    setIsVerifying(true);

    try {
      await userApiClient.post("/auth/verify-email", { email, code });

      toast.success(t("common.verificationSuccess"));
      setTimeout(() => {
        window.location.href = "/app";
      }, 500);
    } catch (error) {
      console.error("验证失败:", error);
      if (error instanceof ApiException) {
        toast.error(error.message);
      } else {
        toast.error(t("common.verificationFailed"));
      }
      // 清空输入框
      codeRefs.forEach((ref) => {
        if (ref.current) ref.current.value = "";
      });
      codeRefs[0].current?.focus();
    } finally {
      setIsVerifying(false);
    }
  };

  // 重新发送验证码
  const handleResend = async () => {
    setIsResending(true);

    try {
      await userApiClient.post("/auth/resend-verification", { email });

      toast.success(t("verification.codeSent"));
      setCountdown(60);
    } catch (error) {
      console.error("重发失败:", error);
      if (error instanceof ApiException) {
        toast.error(error.message);
      } else {
        toast.error(t("common.networkError"));
      }
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-8 sm:py-12">
      <div className="w-[300px] flex flex-col">
        {/* 图标 */}
        <div className="flex justify-center mb-6">
          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
            <Mail className="w-6 h-6 text-primary" />
          </div>
        </div>

        {/* 标题区域 */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-semibold tracking-tight text-foreground mb-2">
            {t("verification.title")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {email
              ? t("verification.subtitle", { email })
              : t("verification.subtitleNoEmail", "请输入您的邮箱和验证码")}
          </p>
        </div>

        {/* 验证码输入 */}
        <div className="space-y-6">
          {!searchParams.get("email") && (
            <div className="space-y-2">
              <Label htmlFor="email" className="sr-only">
                {t("international.emailLabel")}
              </Label>
              <Input
                id="email"
                type="email"
                placeholder={t("register.emailPlaceholder", "请输入您的邮箱")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="text-center"
              />
            </div>
          )}

          <div className="space-y-3">
            <Label className="text-sm text-muted-foreground text-center block">
              {t("verification.codeLabel")}
            </Label>
            <div className="flex gap-3 justify-center">
              {codeRefs.map((ref, index) => (
                <Input
                  key={index}
                  ref={ref}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  className="w-14 h-14 text-center text-2xl font-semibold border-border focus:border-foreground focus:ring-1 focus:ring-foreground"
                  onInput={(e) => handleInput(index, e.currentTarget.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                />
              ))}
            </div>
          </div>

          {/* 验证按钮 */}
          <Button
            onClick={handleVerify}
            disabled={isVerifying}
            className="w-full h-10 bg-foreground text-background hover:bg-foreground/90 font-medium"
          >
            {isVerifying ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />{" "}
                {t("verification.verifying")}
              </>
            ) : (
              t("verification.verifyButton")
            )}
          </Button>

          {/* 重发验证码 */}
          <div className="text-center">
            <button
              onClick={handleResend}
              disabled={countdown > 0 || isResending}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {countdown > 0
                ? `${countdown}s`
                : isResending
                  ? t("verification.resending")
                  : t("verification.resendCode")}
            </button>
          </div>
        </div>

        {/* 返回注册 */}
        <Link
          to="/register"
          className="flex items-center justify-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors mt-8"
        >
          <ArrowLeft className="w-4 h-4" />
          {t("verification.changeEmail")}
        </Link>
      </div>
    </div>
  );
}
